import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from 'motion/react';
import { Server, DollarSign as DollarIcon, TrendingUp, AlertTriangle, Clock, TrendingDown, Zap, Target, Users } from 'lucide-react';
import awsMiddlewareVideo from '../assets/images/Marketing/AWS Middleware for Salesforce Data Cloud.mov';
import AWSMiddleware from '../assets/images/Marketing/AWS-Middleware.jpg';
import Image from 'react-bootstrap/Image';

const AWSMiddlewareSalesforceDataCloud = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        .aws-middleware-page { font-family: 'Outfit', sans-serif; }
        .aws-middleware-page * { font-family: 'Outfit', sans-serif; }

        .hero-section {
          padding: 80px 0;
          background: #fff;
          position: relative;
          overflow: hidden;
        }

        .hero-image-wrapper {
          position: relative;
          display: inline-block;
          z-index: 1;
          width: 100%;
        }

        .hero-shape {
          position: absolute;
          z-index: -1;
          opacity: 0.1;
          animation: float 6s ease-in-out infinite;
        }

        .hero-shape-square {
          width: 120px;
          height: 120px;
          background: #ee0000;
          top: -15px;
          right: -15px;
          border-radius: 20px;
          transform: rotate(15deg);
        }

        .hero-shape-square-2 {
          width: 100px;
          height: 100px;
          background: #067bb1;
          bottom: -20px;
          left: -20px;
          border-radius: 16px;
          transform: rotate(-15deg);
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(15deg); }
          50% { transform: translate(10px, -20px) rotate(20deg); }
        }

        @keyframes float-tri {
          0%, 100% { transform: translate(0, 0) rotate(-15deg); }
          50% { transform: translate(-15px, 15px) rotate(-10deg); }
        }
        
        .hero-tag { 
          display:inline-flex; 
          align-items:center; 
          gap:8px; 
          padding:6px 12px; 
          border-radius:999px; 
          background:#ffefef; 
          color:#ee0000; 
          border:1px solid #ffd4d4; 
          font-weight:700; 
          font-size:12px; 
          margin-bottom: 16px;
        }

        .hero-title {
          font-size: 42px;
          font-weight: 800;
          color: #000;
          margin-bottom: 12px;
          line-height: 1.1;
          letter-spacing: -0.02rem;
        }
        
        .highlight-red { color:#ee0000; }

        .hero-subtitle {
          font-size: 20px;
          color: #000;
          font-weight: 700;
          margin-bottom: 24px;
          letter-spacing: -0.01rem;
        }

        .hero-chip-row { 
          display:flex; 
          gap:12px; 
          flex-wrap:wrap; 
          margin:24px 0; 
        }

        .stat-card { 
          display:flex; 
          align-items:center; 
          gap:12px; 
          padding:12px 16px; 
          border-radius:16px; 
          background:#fff; 
          border:1px solid #e6edf5; 
          box-shadow:0 2px 0 rgba(0,0,0,0.04); 
        }
        
        .stat-icon { 
          width:36px; 
          height:36px; 
          border-radius:12px; 
          display:flex; 
          align-items:center; 
          justify-content:center; 
          border:1px solid transparent; 
        }
        
        .stat-icon-green { background:#f0fff5; border-color:#d7fbe7; color:#10b981; }
        .stat-icon-orange { background:#fff7ed; border-color:#fde2c5; color:#f59e0b; }
        .stat-icon-pink { background:#fdf2f6; border-color:#f8d7e5; color:#ef4444; }
        
        .stat-label { font-size:12px; color:#64748b; font-weight: 500; }
        .stat-value { font-size:16px; font-weight:800; color:#0f172a; }

        .hero-description {
          font-size: 16px;
          line-height: 1.6;
          color: #334155;
          margin-bottom: 32px;
          max-width: 90%;
        }

        .btn-red { 
          text-decoration:none;
          background:#ee0000; 
          color:#fff; 
          border:none; 
          border-radius:12px; 
          padding:12px 24px; 
          font-weight:700; 
          box-shadow:0 12px 24px rgba(238,0,0,0.18); 
          transition: all 0.3s ease;
        }
        .btn-red:hover {
          background: #cc0000;
          transform: translateY(-2px);
          color: #fff;
        }
        
        .btn-outline-dark { 
          text-decoration:none;
          background:#fff; 
          color:#0f172a; 
          border:1px solid #e5e7eb; 
          border-radius:12px; 
          padding:12px 24px; 
          font-weight:700; 
          transition: all 0.3s ease;
        }
        .btn-outline-dark:hover {
          background: #f8fafc;
          transform: translateY(-2px);
        }

        .hero-video-container {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 50px rgba(0,0,0,0.1);
          margin: auto;
        }

        .problem-solution-section {
          background: #fff;
          padding: 80px 0;
          color: #000;
          position: relative;
          overflow: hidden;
        }
        
        /* Decorative Background Elements from GCC */
        .ps-bg-orb {
          position: absolute;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(227, 24, 55, 0.05) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(100px);
          z-index: 0;
          pointer-events: none;
        }
        .ps-orb-tl { top: -100px; left: -100px; }
        .ps-orb-br { bottom: -100px; right: -100px; }

        .ps-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border-radius: 999px;
          background: #ffefef;
          color: #ee0000;
          border: 1px solid #ffd4d4;
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .ps-card {
          background: #fff;
          border-radius: 24px;
          padding: 40px;
          height: 100%;
          position: relative;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          border: 2px solid #e5e7eb;
          transition: all 0.5s ease;
          z-index: 1;
          overflow: hidden;
        }
        .ps-card:hover {
          border-color: rgba(227, 24, 55, 0.5);
          box-shadow: 0 25px 50px -12px rgba(227, 24, 55, 0.15);
          transform: translateY(-5px);
        }

        .ps-card-halo {
          position: absolute;
          inset: -1px;
          background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.1), transparent 70%);
          opacity: 0;
          filter: blur(24px);
          border-radius: 24px;
          z-index: -1;
          transition: opacity 0.7s ease;
        }
        .ps-card:hover .ps-card-halo { opacity: 1; }

        .ps-title {
          font-size: 42px;
          font-weight: 800;
          color: #000;
          margin-bottom: 15px;
          letter-spacing: -0.02em;
        }
        .ps-text {
          font-size: 18px;
          line-height: 1.6;
          color: #475569;
          margin-bottom: 20px;
        }
        .ps-list {
          list-style: none;
          padding-left: 0;
          margin-bottom: 24px;
        }
        .ps-list li {
          font-size: 17px;
          margin-bottom: 16px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          color: #374151;
          font-weight: 500;
          transition: color 0.3s ease;
        }
        .ps-list li:hover { color: #000; }
        
        .ps-bullet {
          width: 6px;
          height: 6px;
          background-color: #ee0000;
          border-radius: 50%;
          margin-top: 10px;
          flex-shrink: 0;
          transition: all 0.3s ease;
          position: relative;
        }
        .ps-list li:hover .ps-bullet {
          transform: scale(1.5);
          box-shadow: 0 0 8px rgba(238, 0, 0, 0.5);
        }

        /* Problem Card Modern Revamp (GCC Style) */
        .problem-card-modern {
          background: #fff;
          border-radius: 24px;
          padding: 32px 24px;
          border: 1px solid #e2e8f0;
          height: 100%;
          position: relative;
          overflow: hidden;
          transition: all 0.4s ease;
          z-index: 1;
        }
        .problem-card-modern:hover {
          border-color: #fee2e2;
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.06);
        }
        .problem-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          position: relative;
          z-index: 2;
          background: #ee0000;
          color: #fff;
          box-shadow: 0 8px 20px rgba(238, 0, 0, 0.25);
          transition: transform 0.3s ease;
        }
        .problem-card-modern:hover .problem-icon-box {
          transform: scale(1.1) rotate(-5deg);
        }
        .problem-card-title {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
          transition: color 0.3s ease;
          padding-bottom: 0px;
        }
        .problem-card-modern:hover .problem-card-title {
          color: #ee0000;
        }
        .problem-card-text {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
        }
        
        /* Decorative Shapes from GCC */
        .shape-tr-circle { 
            position: absolute; 
            top: -60px; 
            right: -60px; 
            width: 150px; 
            height: 150px; 
            border-radius: 50%; 
            background: transparent;
            border: 1px solid rgba(238, 0, 0, 0.1);
            z-index: 0;
            pointer-events: none;
        }
        .shape-tr-inner {
            position: absolute;
            top: -30px;
            right: -30px;
            width: 120px;
            height: 120px;
            border-radius: 50%;
            background: radial-gradient(circle at center, rgba(238, 0, 0, 0.04) 0%, transparent 70%);
            z-index: 0;
        }
        .shape-diamond { 
            position: absolute; 
            top: 20px; 
            right: 20px; 
            width: 12px; 
            height: 12px; 
            background: #fee2e2; 
            transform: rotate(45deg); 
            opacity: 0; 
            transition: all 0.4s ease; 
        }
        .problem-card-modern:hover .shape-diamond { 
            opacity: 1; 
            transform: rotate(45deg) translate(10px, -10px); 
        }

        /* Solution Section Modern Revamp */
        .solution-section {
          padding: 80px 0;
          position: relative;
          overflow: hidden;
        }
        
        .solution-container {
          position: relative;
          background: #fff;
          border-radius: 32px;
          padding: 60px;
          border: 1px solid rgba(56, 164, 229, 0.2);
          box-shadow: 0 20px 50px -12px rgba(24, 132, 227, 0.1);
          z-index: 1;
        }

        .sol-icon-wrapper {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: linear-gradient(135deg, #ee0000, #cc0000);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          margin-bottom: 24px;
          box-shadow: 0 10px 20px rgba(238, 0, 0, 0.2);
        }

        .solution-highlight {
          color: #ee0000;
          font-weight: 700;
        }

        /* Key Capabilities Section (Revamped to match GCC Why Section) */
        .capabilities-section {
          padding: 80px 0;
          background-color: #effdff;
          position: relative;
          overflow: hidden;
        }
        
        .cap-orb-1 {
          position: absolute;
          top: 5%;
          left: -10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(238, 0, 0, 0.05) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(60px);
          z-index: 0;
          pointer-events: none;
        }
        
        .cap-orb-2 {
          position: absolute;
          bottom: 10%;
          right: -5%;
          width: 384px;
          height: 384px;
          background: radial-gradient(circle, rgba(238, 0, 0, 0.05) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(50px);
          z-index: 0;
          pointer-events: none;
        }
        
        .cap-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          background: #fff;
          border: 1px solid rgba(238, 0, 0, 0.2);
          border-radius: 999px;
          color: #ee0000;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.05em;
          margin-bottom: 24px;
          box-shadow: 0 2px 10px rgba(238, 0, 0, 0.05);
        }
        
        .cap-badge-dot {
          width: 6px;
          height: 6px;
          background-color: #ee0000;
          border-radius: 50%;
          box-shadow: 0 0 8px #ee0000;
        }
        
        .cap-card-modern {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 24px;
          padding: 40px 32px;
          position: relative;
          overflow: hidden;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          height: 100%;
          z-index: 1;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
          border: 2px solid #e5e7eb;
        }
        
        .cap-card-modern:hover {
          border-color: rgba(238, 0, 0, 0.3);
          transform: translateY(-5px);
          box-shadow: 0 20px 40px -12px rgba(238, 0, 0, 0.15);
        }
        
        .cap-card-halo {
          position: absolute;
          inset: -2px;
          border-radius: 26px;
          background: radial-gradient(circle at 50% 0%, rgba(238, 0, 0, 0.15), transparent 70%);
          opacity: 0;
          transition: opacity 0.7s ease;
          z-index: -1;
          pointer-events: none;
        }
        .cap-card-modern:hover .cap-card-halo {
          opacity: 1;
        }
        
        .cap-card-shine {
          position: absolute;
          top: 0;
          left: -100%;
          width: 200%;
          height: 100%;
          background: linear-gradient(115deg, transparent 40%, rgba(238, 0, 0, 0.02) 45%, rgba(238, 0, 0, 0.04) 50%, rgba(238, 0, 0, 0.02) 55%, transparent 60%);
          transition: left 0.5s ease;
          z-index: 0;
          pointer-events: none;
          opacity: 0;
        }
        .cap-card-modern:hover .cap-card-shine {
          left: 100%;
          opacity: 1;
        }
        
        .cap-corner-l {
          position: absolute;
          top: 24px;
          right: 24px;
          width: 50px;
          height: 50px;
          border-top: 2px solid rgba(238, 0, 0, 0.1);
          border-right: 2px solid rgba(238, 0, 0, 0.1);
          border-top-right-radius: 20px;
          transition: border-color 0.3s ease;
          z-index: 2;
          pointer-events: none;
        }
        .cap-card-modern:hover .cap-corner-l {
          border-color: rgba(238, 0, 0, 0.4);
        }
        
        .cap-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: linear-gradient(135deg, #ee0000, #ee0000);
          box-shadow: 0 4px 12px rgba(238, 0, 0, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 24px;
          color: #fff;
          position: relative;
          z-index: 2;
          transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: 1px solid rgba(238, 0, 0, 0.1);
        }
        .cap-card-modern:hover .cap-icon-box {
          transform: scale(1.1);
          box-shadow: 0 10px 25px rgba(238, 0, 0, 0.5);
        }
        
        .cap-title-modern {
          font-size: 19px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 12px;
          transition: color 0.3s ease;
          line-height: 1.4;
        }
        .cap-card-modern:hover .cap-title-modern {
          color: #ee0000;
        }

        /* Ideal For Section (Revamped to match AppLink) */
        .ideal-section {
          padding: 80px 0;
          background: #fff;
        }

        .hero-subtitles {
  font-size: 20px;
  color: #2c354c;
  font-weight: 400;
  margin-bottom: 24px;
  letter-spacing: -0.01rem;
  max-width: 100%;
}

        .ideal-card {
          background: #fff;
          border: 2px solid #e5e7eb;
          border-radius: 24px;
          padding: 30px 30px;
          position: relative;
          overflow: hidden;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          height: 100%;
          display: flex;
          align-items: center;
        }
        .ideal-card:hover {
          border-color: rgba(238, 0, 0, 0.3);
          transform: translateY(-5px);
          box-shadow: 0 20px 40px -12px rgba(238, 0, 0, 0.15);
        }
        
        .ideal-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          background: linear-gradient(135deg, #ee0000, #ee0000);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 24px;
          color: #fff;
          flex-shrink: 0;
        }

        .ideal-card .shape-diamond { 
          position: absolute; 
          top: 20px; 
          right: 20px; 
          width: 12px; 
          height: 12px; 
          background: #fee2e2; 
          transform: rotate(45deg); 
          opacity: 0; 
          transition: all 0.4s ease; 
        }
        .ideal-card:hover .shape-diamond { 
          opacity: 1; 
          transform: rotate(45deg) translate(10px, -10px); 
        }

        .ideal-card-title {
          font-size: 19px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0;
          line-height: 1.4;
        }

        .cta-section {
          background: #0f172a;
          color: #fff;
          padding: 80px 0;
          text-align: center;
        }
        .btn-custom {
          padding: 12px 32px;
          border-radius: 12px;
          font-weight: 700;
          transition: all 0.3s ease;
          display: inline-block;
          text-decoration: none;
        }
        .btn-primary-custom {
          background: #ee0000;
          color: #fff;
          box-shadow: 0 10px 15px -3px rgba(238,0,0,0.3);
        }
        .btn-primary-custom:hover {
          background: #cc0000;
          transform: translateY(-2px);
          color: #fff;
        }
      `}</style>

      <div className="aws-middleware-page">
        {/* Hero Section */}
        <section className="hero-section">
          <Container>
            <Row className="align-items-center justify-content-between">
              <Col lg={7}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {/* <div className="hero-tag mb-3">⚡ AWS Middleware Layer</div> */}
                  {/* <h1 className="hero-title mb-2">
                    Reduce Salesforce Data Cloud Processing Costs by
                    <span className="highlight-red"> Up to 40%</span>
                  </h1> */}
                  <h1 className="hero-title mb-2">
                    AWS Middleware
                  </h1>
                  <p className="hero-subtitles text-black ls-minus-045 mb-3">An AWS-native middleware layer that pre-processes and optimizes data before ingestion into Salesforce Data Cloud.</p>



                  {/* <p className="hero-description">
                    An AWS-native middleware layer that pre-processes and optimizes data before ingestion into Salesforce Data Cloud.
                  </p> */}

                  <div className="d-flex gap-3 mt-4">
                    <a href="/contact" className="btn-red">Book Architecture Consultation</a>
                  </div>
                </motion.div>
              </Col>
              <Col lg={5} className="text-center">
                <div className="hero-image-wrapper">
                  <div className="hero-shape hero-shape-square"></div>
                  <div className="hero-shape hero-shape-square-2" style={{ animationName: 'float-tri' }}></div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <div className="hero-video-container" style={{ width: "100%" }}>
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        className="w-100"
                        style={{ borderRadius: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                      >
                        <source src={awsMiddlewareVideo} />
                      </video>
                    </div>
                  </motion.div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Problem Section */}
        <section className="problem-solution-section" id="problem" style={{ background: 'rgb(226, 241, 254)' }}>
          <div className="ps-bg-orb ps-orb-tl"></div>
          <div className="ps-bg-orb ps-orb-br"></div>
          <Container>
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="text-center mb-5">
                  {/* <div className="ps-pill">The Challenge</div> */}
                  <h2 className="ps-title">Business Challenges</h2>
                  <p className="ps-text">
                    Organizations often send raw data directly into Data Cloud, leading to:
                  </p>
                </div>

                <Row className="g-4 mb-5">
                  {[
                    { icon: <DollarIcon size={28} />, title: "High Data Cloud credit consumption" },
                    { icon: <Zap size={28} />, title: "Slow ingestion pipelines" },
                    { icon: <AlertTriangle size={28} />, title: "Inconsistent data formats" },
                    { icon: <TrendingDown size={28} />, title: "Limited preprocessing capabilities" }
                  ].map((item, idx) => (
                    <Col key={idx} md={6} xl={3}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className="problem-card-modern"
                      >
                        <div className="shape-tr-circle"></div>
                        <div className="shape-tr-inner"></div>
                        <div className="shape-diamond"></div>
                        <div className="problem-icon-box">
                          {item.icon}
                        </div>
                        <h4 className="problem-card-title mb-0">{item.title}</h4>
                        {/* <p className="problem-card-text mb-0">{item.desc}</p> */}
                      </motion.div>
                    </Col>
                  ))}
                </Row>

                <div className="text-center">
                  <p className="ps-text fs-4 mb-0">
                    This results in <span className="solution-highlight">higher costs and inefficient processing pipelines.</span>
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Solution Section */}
        <section className="solution-section bg-white" id="solution">
          <div className="ps-bg-orb ps-orb-tl"></div>
          <div className="ps-bg-orb ps-orb-br"></div>
          <Container>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="solution-container"
            >
              <Row className="align-items-center">
                <Col lg={8}>
                  <h2 className="ps-title">Comsense Solution</h2>
                  <p className="ps-text fs-5 mb-4">
                    Our <span className="solution-highlight">AWS Middleware for Salesforce Data Cloud</span> introduces a scalable pre-processing layer built using AWS-native services.
                  </p>
                  <p className="ps-text fs-5 mb-0">
                    This layer transforms, enriches, and structures datasets before ingestion, ensuring <span className="solution-highlight">only optimized, activation-ready data</span> is processed.
                  </p>
                </Col>
                <Col lg={4} className="d-none d-lg-flex justify-content-end">

                  <Image src={AWSMiddleware} alt="The Solution" className="img-fluid" />


                </Col>
              </Row>
            </motion.div>
          </Container>
        </section>

        {/* Key Capabilities Section */}
        <section className="capabilities-section" id="capabilities">
          <div className="cap-orb-1"></div>
          <div className="cap-orb-2"></div>
          <Container>
            <div className="text-center mb-5">
              {/* <div className="cap-badges bg-white ps-pill">
                <div className="cap-badge-dot"></div>
                Core Features
              </div> */}
              <h2 className="ps-title">Key Capabilities</h2>
            </div>

            <Row className="g-4">
              {[
                { icon: <Zap size={28} />, text: "Pre-ingestion data transformation and enrichment" },
                { icon: <TrendingUp size={28} />, text: "Up to 40% reduction in Data Cloud credit consumption" },
                { icon: <Clock size={28} />, text: "Real-time and batch processing support" },
                { icon: <Server size={28} />, text: "Serverless scalability with AWS Lambda, Glue, S3, and Redshift" }
              ].map((item, idx) => (
                <Col key={idx} md={6} xl={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="cap-card-modern"
                  >
                    <div className="cap-card-halo"></div>
                    <div className="cap-card-shine"></div>
                    <div className="cap-corner-l"></div>
                    <div className="cap-icon-box">
                      {item.icon}
                    </div>
                    <h4 className="cap-title-modern">{item.text}</h4>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Ideal For Section */}
        <section className="ideal-section" id="ideal">
          <div className="ideal-orb-1"></div>
          <Container>
            <div className="text-center mb-5">
              {/* <div className="cap-badges ps-pill">
                <div className="cap-badge-dot"></div>
                Target Audience
              </div> */}
              <h2 className="ps-title">Ideal For</h2>
            </div>

            <Row className="justify-content-center g-4">
              <Col md={5}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="ideal-card"
                >
                  <div className="ideal-card-halo"></div>
                  <div className="ideal-card-shine"></div>
                  <div className="ideal-corner-l"></div>
                  <div className="ideal-icon-box">
                    <Target size={32} />
                  </div>
                  <h3 className="ideal-card-title mb-0">Data teams seeking {/* <span style={{ color: '#ee0000' }}>  */} cost optimization and pipeline efficiency
                    {/* </span> */}
                  </h3>
                </motion.div>
              </Col>

              <Col md={5}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  className="ideal-card"
                >
                  <div className="ideal-card-halo"></div>
                  <div className="ideal-card-shine"></div>
                  <div className="ideal-corner-l"></div>
                  <div className="ideal-icon-box">
                    <Users size={32} />
                  </div>
                  <h3 className="ideal-card-title mb-0">Enterprises running
                    {/* <span style={{ color: '#ee0000' }}>  */}
                    large-scale Data Cloud implementations
                    {/* </span> */}
                  </h3>
                </motion.div>
              </Col>
            </Row>

            <div className="text-center mt-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <a href="/contact" className="btn-custom btn-primary-custom">Optimize Data Cloud Now</a>
              </motion.div>
            </div>
          </Container>
        </section>


      </div>
    </>
  );
};

export default AWSMiddlewareSalesforceDataCloud;

